"use client";

import type { JSX } from "react";
import { useCallback, useEffect, useRef, useState } from "react";
import { Button, Icon, Text, View } from "@/components";
import { useLayout } from "@/providers";

/** Cada cuántos segundos de reproducción se reporta el avance al servidor. */
const REPORT_INTERVAL_SEG = 10;

/**
 * Margen local (seg) sobre la posición máxima vista antes de clampear un seek.
 * El árbitro real es el servidor (rechaza saltos > tolerancia); esto solo evita
 * que el usuario vea un seek "aceptado" que después el servidor va a rechazar.
 */
const SEEK_GUARD_SEG = 1;

/** Cada cuántos ms se sondea la posición del reproductor (heartbeat + anti-avance). */
const POLL_INTERVAL_MS = 500;

/**
 * Paso (seg) de los botones de retroceder y adelantar. Retroceder siempre está
 * permitido; adelantar solo se ofrece cuando el curso no restringe el avance.
 */
const SEEK_STEP_SEG = 10;

const IFRAME_API_SRC = "https://www.youtube.com/iframe_api";

/** Reproductor de YouTube (subconjunto de la IFrame Player API que consumimos). */
interface YouTubePlayer {
  getCurrentTime(): number;
  seekTo(seconds: number, allowSeekAhead: boolean): void;
  playVideo(): void;
  pauseVideo(): void;
  destroy(): void;
}

interface YouTubeNamespace {
  Player: new (
    el: HTMLElement,
    opts: {
      videoId: string;
      playerVars?: Record<string, number | string>;
      events?: {
        onReady?: (e: { target: YouTubePlayer }) => void;
        onStateChange?: (e: { data: number; target: YouTubePlayer }) => void;
      };
    }
  ) => YouTubePlayer;
  PlayerState: {
    PLAYING: number;
    PAUSED: number;
    ENDED: number;
    BUFFERING: number;
  };
}

declare global {
  interface Window {
    YT?: YouTubeNamespace;
    onYouTubeIframeAPIReady?: () => void;
  }
}

/** Fallbacks webkit (Safari) de la Fullscreen API, ausentes en los tipos del DOM. */
interface FullscreenDocument extends Document {
  webkitFullscreenElement?: Element | null;
  webkitFullscreenEnabled?: boolean;
  webkitExitFullscreen?: () => void;
}

interface FullscreenContainer extends HTMLElement {
  webkitRequestFullscreen?: () => void;
}

/** Extrae el id de 11 caracteres de una URL de YouTube (watch, youtu.be o embed). */
const extractYouTubeId = (url: string): string | null => {
  const patterns = [
    /[?&]v=([\w-]{11})/,
    /youtu\.be\/([\w-]{11})/,
    /\/embed\/([\w-]{11})/
  ];
  for (const pattern of patterns) {
    const match = url.match(pattern);
    if (match?.[1]) return match[1];
  }
  return null;
};

/**
 * Carga el script de la IFrame Player API y resuelve cuando está listo. Rechaza si
 * el script no puede cargarse (red caída, bloqueador o CSP sin `www.youtube.com`),
 * para que el player muestre un error en lugar de quedarse en negro. Un script de
 * un intento anterior que nunca inicializó `window.YT` se considera muerto y se
 * reemplaza (reintento).
 */
const loadIframeApi = (): Promise<YouTubeNamespace> => {
  return new Promise((resolve, reject) => {
    if (window.YT?.Player) {
      resolve(window.YT);
      return;
    }
    const prev = window.onYouTubeIframeAPIReady;
    window.onYouTubeIframeAPIReady = () => {
      prev?.();
      if (window.YT) resolve(window.YT);
    };
    document
      .querySelector<HTMLScriptElement>(`script[src="${IFRAME_API_SRC}"]`)
      ?.remove();
    const script = document.createElement("script");
    script.src = IFRAME_API_SRC;
    script.onerror = () =>
      reject(new Error("No se pudo cargar la IFrame API de YouTube"));
    document.head.appendChild(script);
  });
};

/** Formatea segundos como `m:ss` (o `h:mm:ss` si supera la hora). */
const formatoTiempo = (totalSeg: number): string => {
  const seg = Math.max(0, Math.floor(totalSeg));
  const s = seg % 60;
  const m = Math.floor(seg / 60) % 60;
  const h = Math.floor(seg / 3600);
  const mmss = `${m}:${String(s).padStart(2, "0")}`;
  return h > 0
    ? `${h}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`
    : mmss;
};

export interface VideoPlayerProps {
  /** URL del video en YouTube (watch, youtu.be o embed). */
  url: string;
  /** Duración total en segundos (referencia del backend). */
  duracionSeg: number;
  /** Posición desde la que se reanuda (video_pos_seg); solo se lee al crear el player. */
  posicionInicialSeg: number;
  /**
   * Posición máxima ya alcanzada (video_max_pos_seg); rige el anti-avance.
   * Solo se lee al crear el player: durante la sesión el máximo se trackea localmente.
   */
  maxPosSeg: number;
  /** Si el video ya está completo, se permite navegarlo libremente (repaso). */
  completed: boolean;
  /**
   * Si el curso restringe adelantar el video. En `false` el alumno puede saltar
   * hacia adelante (el servidor tampoco lo rechaza). Por defecto `true`.
   */
  restringeAvance?: boolean;
  /**
   * Reporte de avance: se invoca con la posición actual (seg) periódicamente y
   * al pausar/terminar. La página lo envía a `POST …/video-progress`; la marca
   * de "video completo" la decide siempre el servidor.
   */
  onProgress: (posicionSeg: number) => void;
}

/**
 * @name VideoPlayer
 * @description Reproductor del módulo (A5) sobre la IFrame Player API de YouTube:
 * recibe una URL de YouTube y emite eventos de avance. Aplica el anti-avance del
 * dominio (no se puede adelantar más allá de lo ya visto; pausar y retroceder sí)
 * como cortesía de UX cuando el curso lo restringe — la validación autoritativa
 * vive en el servidor.
 *
 * El chrome de YouTube va oculto (`controls: 0`, teclado deshabilitado) y un
 * overlay propio captura todos los clics para que no se pueda abrir el título,
 * las sugerencias de pausa ni "Ver en YouTube". Los únicos controles son los
 * nuestros: play/pausa, retroceder {@link SEEK_STEP_SEG} s (siempre permitido),
 * adelantar {@link SEEK_STEP_SEG} s (cuando el curso NO restringe el avance, o en
 * repaso con el video ya completo — la restricción aplica solo a lo no visto),
 * el tiempo transcurrido y pantalla completa. No hay barra de progreso clickeable:
 * el video se navega con retroceder/adelantar.
 *
 * La pantalla completa es propia (Fullscreen API sobre el contenedor del player,
 * no el `fs` de YouTube, que va deshabilitado): así los controles y el overlay
 * anti-clic siguen operativos también en fullscreen. En navegadores sin soporte
 * (p. ej. iPhone, donde solo un `<video>` nativo puede ir a fullscreen) el botón
 * no se muestra.
 *
 * La reproducción arranca automáticamente al abrir el módulo (reanudando desde
 * la última posición). Si el navegador bloquea el autoplay con sonido (política
 * de autoplay, p. ej. al entrar por URL directa sin interacción previa), el video
 * queda en pausa con el botón de play visible.
 *
 * @param {VideoPlayerProps} props - URL de YouTube, posiciones de reanudación,
 * flag de restricción y callback de avance.
 * @returns {JSX.Element} El reproductor renderizado.
 */
const VideoPlayer = ({
  url,
  duracionSeg,
  posicionInicialSeg,
  maxPosSeg,
  completed,
  restringeAvance = true,
  onProgress
}: VideoPlayerProps): JSX.Element => {
  const { t } = useLayout();
  const mountRef = useRef<HTMLDivElement | null>(null);
  const playerRef = useRef<YouTubePlayer | null>(null);
  /** Contenedor completo del player (video + controles): es lo que va a fullscreen. */
  const containerRef = useRef<HTMLElement | null>(null);
  const [enFullscreen, setEnFullscreen] = useState(false);
  /** `false` hasta montar (SSR) o si el navegador no soporta la Fullscreen API. */
  const [fullscreenDisponible, setFullscreenDisponible] = useState(false);
  /** El script de la IFrame API no cargó (red/bloqueador/CSP); permite reintentar. */
  const [loadError, setLoadError] = useState(false);
  const [retryKey, setRetryKey] = useState(0);
  /** Estado del player para los controles propios (overlay y barra inferior). */
  const [reproduciendo, setReproduciendo] = useState(false);
  const [posicionSeg, setPosicionSeg] = useState(posicionInicialSeg);
  /** Máxima posición alcanzada en esta sesión + lo que ya sabía el servidor. */
  const maxSeenRef = useRef<number>(maxPosSeg);
  const lastReportRef = useRef<number>(0);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Valores vivos para los callbacks de la API (evita cerrar sobre props viejas).
  // Las posiciones y la duración también van por refs: cada reporte de avance
  // refresca el detalle del módulo en la página y estas props vuelven cambiadas;
  // si fueran dependencias del efecto, el player se destruiría y recrearía en
  // cada heartbeat (video que "recarga" cada ~10 s). Solo se leen al montar.
  const completoRef = useRef(completed);
  const restringeRef = useRef(restringeAvance);
  const onProgressRef = useRef(onProgress);
  const duracionRef = useRef(duracionSeg);
  const posicionInicialRef = useRef(posicionInicialSeg);
  const maxPosRef = useRef(maxPosSeg);
  completoRef.current = completed;
  restringeRef.current = restringeAvance;
  onProgressRef.current = onProgress;
  duracionRef.current = duracionSeg;
  posicionInicialRef.current = posicionInicialSeg;
  maxPosRef.current = maxPosSeg;

  const report = useCallback((posicion: number) => {
    lastReportRef.current = posicion;
    onProgressRef.current(Math.floor(posicion));
  }, []);

  const videoId = extractYouTubeId(url);

  const alternarReproduccion = () => {
    const player = playerRef.current;
    if (!player) return;
    if (reproduciendo) player.pauseVideo();
    else player.playVideo();
  };

  const retroceder = () => {
    const player = playerRef.current;
    if (!player) return;
    const destino = Math.max(0, player.getCurrentTime() - SEEK_STEP_SEG);
    player.seekTo(destino, true);
    setPosicionSeg(Math.floor(destino));
  };

  const alternarFullscreen = () => {
    const doc = document as FullscreenDocument;
    if (doc.fullscreenElement ?? doc.webkitFullscreenElement) {
      if (doc.exitFullscreen) doc.exitFullscreen().catch(() => undefined);
      else doc.webkitExitFullscreen?.();
      return;
    }
    const container = containerRef.current as FullscreenContainer | null;
    if (!container) return;
    if (container.requestFullscreen)
      container.requestFullscreen().catch(() => undefined);
    else container.webkitRequestFullscreen?.();
  };

  // Soporte y estado de fullscreen: el estado sale del evento (no del click),
  // así también cubre la salida con Escape o gesto del navegador.
  useEffect(() => {
    const doc = document as FullscreenDocument;
    setFullscreenDisponible(
      doc.fullscreenEnabled || doc.webkitFullscreenEnabled === true
    );
    const sync = () =>
      setEnFullscreen(
        Boolean(doc.fullscreenElement ?? doc.webkitFullscreenElement)
      );
    document.addEventListener("fullscreenchange", sync);
    document.addEventListener("webkitfullscreenchange", sync);
    return () => {
      document.removeEventListener("fullscreenchange", sync);
      document.removeEventListener("webkitfullscreenchange", sync);
    };
  }, []);

  /**
   * Disponible cuando el curso no restringe el avance o el video ya está completo
   * (repaso); en el resto de los casos el botón no se renderiza.
   */
  const adelantar = () => {
    const player = playerRef.current;
    if (!player) return;
    const destino = Math.min(
      duracionSeg,
      player.getCurrentTime() + SEEK_STEP_SEG
    );
    player.seekTo(destino, true);
    setPosicionSeg(Math.floor(destino));
  };

  // Deps parciales a propósito: retryKey re-dispara el montaje del player tras un
  // error de carga; posiciones/duración entran por refs (ver arriba). Iba con un
  // `biome-ignore` de useExhaustiveDependencies, pero el biome.json del template
  // v2 trae esa regla en "off" y la supresión quedaba marcada como inútil.
  useEffect(() => {
    if (!videoId || !mountRef.current) return;
    let cancelled = false;
    maxSeenRef.current = maxPosRef.current;
    lastReportRef.current = 0;

    const clampAntiSeek = () => {
      const player = playerRef.current;
      if (!player) return;
      const actual = player.getCurrentTime();
      setPosicionSeg(Math.floor(actual));
      const restringido = restringeRef.current && !completoRef.current;
      if (restringido && actual > maxSeenRef.current + SEEK_GUARD_SEG) {
        // Anti-avance: devuelve la reproducción a lo ya visto (pausar/retroceder sí).
        player.seekTo(maxSeenRef.current, true);
        return;
      }
      if (actual > maxSeenRef.current) maxSeenRef.current = actual;
      // Heartbeat: reporta cada REPORT_INTERVAL_SEG segundos de reproducción.
      if (actual - lastReportRef.current >= REPORT_INTERVAL_SEG) report(actual);
    };

    loadIframeApi()
      .then((YT) => {
        if (cancelled || !mountRef.current) return;
        playerRef.current = new YT.Player(mountRef.current, {
          videoId,
          // Sin chrome de YouTube: los controles son el overlay y la barra propios.
          playerVars: {
            rel: 0,
            modestbranding: 1,
            playsinline: 1,
            controls: 0,
            disablekb: 1,
            fs: 0,
            iv_load_policy: 3,
            autoplay: 1
          },
          events: {
            onReady: ({ target }) => {
              if (posicionInicialRef.current > 0)
                target.seekTo(posicionInicialRef.current, true);
              // Autoplay al abrir el módulo; si el navegador lo bloquea (política
              // de autoplay), el video queda en pausa listo para reproducir.
              target.playVideo();
              pollRef.current = setInterval(clampAntiSeek, POLL_INTERVAL_MS);
            },
            onStateChange: ({ data, target }) => {
              setReproduciendo(
                data === YT.PlayerState.PLAYING ||
                  data === YT.PlayerState.BUFFERING
              );
              if (data === YT.PlayerState.ENDED) {
                // El final se reporta con la duración de referencia: el servidor marca completo.
                report(duracionRef.current);
              } else if (data === YT.PlayerState.PAUSED) {
                report(target.getCurrentTime());
              }
            }
          }
        });
      })
      .catch(() => {
        if (!cancelled) setLoadError(true);
      });

    return () => {
      cancelled = true;
      if (pollRef.current) clearInterval(pollRef.current);
      pollRef.current = null;
      playerRef.current?.destroy();
      playerRef.current = null;
      setReproduciendo(false);
    };
  }, [videoId, report, retryKey]);

  if (!videoId) {
    return (
      <View className="w-full rounded-2xl bg-black/90 text-white text-sm p-6 text-center">
        {t("invalidYoutubeUrl")}
      </View>
    );
  }

  if (loadError) {
    return (
      <View className="w-full rounded-2xl bg-black/90 text-white p-6 flex flex-col items-center gap-4 text-center">
        <Text className="text-sm text-white">
          {t("youtubePlayerLoadError")}
        </Text>
        <Button
          action={() => {
            setLoadError(false);
            setRetryKey((k) => k + 1);
          }}
          label={t("retry")}
          className="px-5 py-2 rounded-full bg-white/10 border border-white/30 text-sm font-medium text-white hover:bg-white/20"
        >
          {t("retry")}
        </Button>
      </View>
    );
  }

  return (
    <View
      ref={containerRef}
      className="w-full rounded-2xl overflow-hidden bg-black flex flex-col"
    >
      {/* En fullscreen el contenedor pasa a ocupar el viewport: el video deja el
          aspect-ratio fijo y se estira a llenar el alto que deja la barra.
          En viewports bajos (teléfono en horizontal, la orientación natural para
          mirar un video) 16:9 sobre el ancho completo da un alto mayor que la
          pantalla y los controles quedan fuera de vista: ahí se acota el alto y
          el video se centra con banda negra a los costados, sobre el mismo
          contenedor negro. */}
      <View
        className={`relative w-full ${enFullscreen ? "flex-1 min-h-0" : "aspect-video [@media(max-height:560px)]:max-h-[70svh]"}`}
      >
        {/* La IFrame API reemplaza este nodo por el iframe del reproductor. */}
        <div ref={mountRef} className="w-full h-full" />
        {/*
         * Overlay a pantalla completa: bloquea todo clic hacia el iframe (título,
         * sugerencias de pausa, "Ver en YouTube") y hace de play/pausa gigante.
         */}
        <Button
          action={alternarReproduccion}
          aria-label={t(reproduciendo ? "videoPause" : "videoPlay")}
          className={`absolute inset-0 z-10 flex items-center justify-center cursor-pointer transition-colors ${
            reproduciendo ? "bg-transparent" : "bg-black/40"
          }`}
        >
          <Icon
            name="fa-solid fa-play"
            className={`w-16 h-16 rounded-full bg-black/60 text-white text-2xl flex items-center justify-center pl-1 transition-opacity ${
              reproduciendo ? "opacity-0" : "opacity-100"
            }`}
          />
        </Button>
      </View>
      {/*
       * El `leading-none!` de estos íconos corrige la caída vertical, no el
       * tamaño: los dejamos en el piso de 25px que impone el template
       * (globals.css + el átomo Icon). FontAwesome acompaña cada clase de
       * tamaño con métricas en `em` (`.fa-xs` trae line-height 0.08333em y
       * vertical-align 0.125em); como el template pisa solo el font-size, esas
       * dos se resuelven contra 25px y la caja del <i> colapsa a ~2px. Se nota
       * acá y no en otros íconos porque estos van dentro de un Button CON
       * `label`, y ese átomo envuelve los children en un <Text>: el <i> queda
       * inline dentro de un span en vez de ser flex item del botón, y ahí el
       * vertical-align sí aplica y lo sube. Medido en el navegador.
       */}
      <View className="flex items-center gap-1 px-3 py-2">
        <Button
          action={alternarReproduccion}
          label={t(reproduciendo ? "videoPause" : "videoPlay")}
          className="w-11 h-11 sm:w-9 sm:h-9 rounded-full text-white hover:bg-white/10 flex items-center justify-center"
        >
          <Icon
            name={reproduciendo ? "fa-solid fa-pause" : "fa-solid fa-play"}
            className="text-sm leading-none!"
          />
        </Button>
        <Button
          action={retroceder}
          label={t("videoRewind")}
          className="w-11 h-11 sm:w-9 sm:h-9 rounded-full text-white hover:bg-white/10 flex items-center justify-center"
        >
          <Icon
            name="fa-solid fa-rotate-left"
            className="text-sm leading-none!"
          />
        </Button>
        {(!restringeAvance || completed) && (
          <Button
            action={adelantar}
            label={t("videoForward")}
            className="w-11 h-11 sm:w-9 sm:h-9 rounded-full text-white hover:bg-white/10 flex items-center justify-center"
          >
            <Icon
              name="fa-solid fa-rotate-right"
              className="text-sm leading-none!"
            />
          </Button>
        )}
        <Text className="ml-auto text-xs text-white/80 tabular-nums">
          {`${formatoTiempo(posicionSeg)} / ${formatoTiempo(duracionSeg)}`}
        </Text>
        {fullscreenDisponible && (
          <Button
            action={alternarFullscreen}
            label={t(enFullscreen ? "videoExitFullscreen" : "videoFullscreen")}
            className="w-11 h-11 sm:w-9 sm:h-9 rounded-full text-white hover:bg-white/10 flex items-center justify-center"
          >
            <Icon
              name={
                enFullscreen ? "fa-solid fa-compress" : "fa-solid fa-expand"
              }
              className="text-sm leading-none!"
            />
          </Button>
        )}
      </View>
    </View>
  );
};

export default VideoPlayer;
